import React, { useContext, useEffect } from "react";
import { Typography, Container, Grid, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AccountContext } from "../../store/AccountContext";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import { OverviewContext } from "../../store/OverviewContext";
import { Link } from "react-router-dom";
import BackdropLoader from "../common/BackdropLoader";
import Countdown from "react-countdown";
import dayjs from "dayjs";
// import DoorsClosingCountdown from "../Banners/DoorsClosingCountdown";
// import { MEMBER_STATUS_STANDARD } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS/MEMBERSHIP ESCALATION CLASS.** This banner is the join call
 * to action on My Account — it reads the live subscription products and shows
 * the discounted price and expiry countdown. (#1246 removed its "or" and
 * "72H Free Trial" link: no trial is started on the website.) The conversion
 * was styling-only: no
 * product lookup, member-status branch, price format, countdown date or handler
 * moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `bodyText`, `countdown` and
 * `countdownUnitFirstWrapper` were declared in the v4 `useStyles` object but
 * never referenced from the JSX, so they rendered nothing. `classes.box` was
 * referenced by the inner `<div>` but **never declared** — it was `undefined`,
 * so React rendered no class attribute; the `<div>` is kept (it is a real
 * layout node) and the `className` is dropped rather than invented.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().background — a bare <div>, which has no `sx` prop. The
// literal `@media (min-width:600px)` / `(min-width:960px)` become
// `breakpoints.up("sm"|"md")`; `themeV5` pins those to the v4 values.
const BannerBackground = styled("div")(({ theme }) => ({
  backgroundImage: `url(https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/freetrial_bg.jpg)`,
  boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.8)",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundAttachment: "scroll",
  width: "100%",
  height: "75vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.up("sm")]: {
    height: "500px",
  },
  [theme.breakpoints.up("md")]: {
    height: "550px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().username
const usernameSx = (theme) => ({
  color: theme.palette.common.white,
  fontSize: "1.4rem",
  lineHeight: "1.4rem",
  textAlign: "left",
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    fontWeight: 600,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().header
const headerSx = (theme) => ({
  color: theme.palette.common.white,
  fontSize: "3.2rem",
  lineHeight: "3rem",
  textAlign: "left",
  fontWeight: 500,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    fontWeight: 600,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3.6rem",
    lineHeight: "3.6rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "4.6rem",
    lineHeight: "4.4rem",
  },
});

// was: useStyles().subHeader
const subHeaderSx = (theme) => ({
  color: "#bbb",
  fontSize: "1.3rem",
  lineHeight: "1.4rem",
  fontWeight: "500",
  textAlign: "left",
  width: "90%",
  textShadow: "-2px 2px 2px #171717",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
  },
});

// was: useStyles().bottomText
const bottomTextSx = (theme) => ({ marginTop: theme.spacing(2) });

// was: useStyles().countdownContainer
const countdownContainerSx = (theme) => ({
  padding: theme.spacing(1, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(0),
  },
});

// was: useStyles().countdownWrapper
const countdownWrapperSx = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
});

// was: useStyles().countdownLabelWrapper
const countdownLabelWrapperSx = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
});

// was: useStyles().countdownUnitWrapper.
//
// `borderRadius: "5px"` is a string, NOT the v4 bare `5` (guide §2, "`sx`
// `borderRadius` is shape-scaled"): JSS's default-unit plugin turned `5` into
// `5px`, but `sx`'s `borderRadius` transform multiplies through
// `shape.borderRadius` (4), which would silently render 20px.
const countdownUnitWrapperSx = (theme) => ({
  textAlign: "center",
  backgroundColor: "rgba(5,5,5,0.7)",
  padding: "2% 0",
  fontSize: "2rem",
  fontWeight: "600",
  color: "#eeeeee",
  maxWidth: "80px",
  margin: "0 0.7%",
  borderRadius: "5px",
  [theme.breakpoints.up("sm")]: {
    padding: "1.8% 0",
    fontSize: "2rem",
    fontWeight: "600",
  },
  [theme.breakpoints.up("md")]: {
    padding: "2% 0",
    fontSize: "2.2rem",
    fontWeight: "600",
  },
});

// was: useStyles().countdownUnitLabel
const countdownUnitLabelSx = (theme) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  textAlign: "center",
  color: theme.palette.common.white,
  maxWidth: "80px",
  margin: "0 0.7%",
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1rem",
  },
});

// was: useStyles().originalPrice
const originalPriceSx = (theme) => ({
  width: "fit-content",
  color: "#ddd",
  letterSpacing: "-1px",
  fontSize: "1.4rem",
  lineHeight: "1.6rem",
  textAlign: "left",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    fontWeight: 600,
    fontSize: "1.3rem",
    lineHeight: "1.4rem",
    marginBottom: theme.spacing(0.5),
  },
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(1.5),
    fontSize: "1.7rem",
    lineHeight: "1.4rem",
  },
});

// was: useStyles().discountPrice
const discountPriceSx = (theme) => ({
  color: theme.palette.background.ffGreen,
  fontSize: "2.8rem",
  lineHeight: "2.2rem",
  letterSpacing: "-1px",
  textAlign: "left",
  fontWeight: 600,
  textShadow: "-1px 1px 1px #171717",
  marginBottom: theme.spacing(2),
  width: "fit-content",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.2rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
    lineHeight: "2.4rem",
  },
});

// PARITY RESTORE (guide §2, "Button default color"): the two "Join Now" CTAs
// carry no v4 rule of their own. v4's plain <Button> defaulted to
// `color="default"` → `color: theme.palette.text.primary`; v5+ deleted that
// colour and made `primary` the default, which would paint the label lime on
// the lime `typography.button` background — an invisible Join Now button on the
// membership CTA.
const joinNowCtaSx = { color: "text.primary" };

const CTABanner = (props) => {
  const {
    userProfile,
    subscriptionProducts,
    getSubscriptionProducts,
    loading,
  } = useContext(AccountContext);

  const { overview } = useContext(OverviewContext);

  useEffect(() => {
    getSubscriptionProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const standardProduct =
    subscriptionProducts &&
    subscriptionProducts?.results.find((p) => p.memberStatusId === "standard");

  const discountedStandardProduct =
    subscriptionProducts &&
    subscriptionProducts?.results.find(
      (p) => p.memberStatusId === "standard" && p.discounted,
    );

  const RenderCountdown = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return <React.Fragment />;
    }
    return (
      <Container sx={countdownContainerSx}>
        <Grid container sx={countdownWrapperSx}>
          {days > 0 ? (
            <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitWrapperSx}>
              <span>
                {days.toString().length === 1 ? "0" : ""}
                {days}
              </span>
            </Grid>
          ) : null}
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitWrapperSx}>
            <span>
              {hours.toString().length === 1 ? "0" : ""}
              {hours}
            </span>
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitWrapperSx}>
            <span>
              {minutes.toString().length === 1 ? "0" : ""}
              {minutes}
            </span>
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitWrapperSx}>
            <span>
              {seconds.toString().length === 1 ? "0" : ""}
              {seconds}
            </span>
          </Grid>
        </Grid>
        <Grid container sx={countdownLabelWrapperSx}>
          {days > 0 ? (
            <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitLabelSx}>
              Days
            </Grid>
          ) : null}
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitLabelSx}>
            Hours
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitLabelSx}>
            Mins
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 4 }} sx={countdownUnitLabelSx}>
            Secs
          </Grid>
        </Grid>
      </Container>
    );
  };

  const BuildCTA = (props) => {
    return (
      <>
        <Typography component="h1" variant="h1" sx={headerSx}>
          {props.title}
        </Typography>
        <Typography component="h2" variant="h2" sx={subHeaderSx}>
          {props.subtitle}
        </Typography>
        {standardProduct && (
          <Link to="/myaccount/join-now" style={{ textDecoration: "none" }}>
            <Button name="Join Now" size="large" sx={joinNowCtaSx}>
              Join Now
            </Button>
          </Link>
        )}
      </>
    );
  };

  const BuildCountdownCTA = (props) => {
    const formatter = new Intl.NumberFormat("en-GB", {
      currency: discountedStandardProduct?.currency,
      style: "currency",
    });
    return (
      <>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Typography
              component="h1"
              variant="h1"
              sx={headerSx}
              style={{ color: "#fff", letterSpacing: "-2px", fontWeight: 600 }}
            >
              Limited Time Discount
            </Typography>
            <Typography component="h2" variant="h2" sx={originalPriceSx}>
              {" "}
              Was{" "}
              {formatter.format(discountedStandardProduct?.discountedOrigCost)}
            </Typography>
            <Typography component="h2" variant="h2" sx={discountPriceSx}>
              {" "}
              Now {formatter.format(discountedStandardProduct?.cost)}
            </Typography>
          </Grid>
        </Grid>
        {discountedStandardProduct.availableUntil ? (
          /* PARITY RESTORE (guide §2, "`<Grid container>` lost its
             `width: 100%`"): v4's `.MuiGrid-container` declared `width: 100%`
             unconditionally and v9 emits no width rule at all. This one renders
             inside a `<Grid>` *item* (block), so it is a no-op today — it is
             restated because the rendered parent is decided by the call site,
             which can move without this file changing. */
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid size={{ xs: 12, sm: 8, md: 6 }}>
              <Countdown
                date={dayjs(discountedStandardProduct.availableUntil).toDate()}
                renderer={RenderCountdown}
                zeroPadTime={2}
                daysInHours={true}
              />
            </Grid>
          </Grid>
        ) : null}
        {standardProduct && (
          <>
            <Link to="/myaccount/join-now" style={{ textDecoration: "none" }}>
              <Button name="Join Now" size="large" sx={joinNowCtaSx}>
                Join Now
              </Button>
            </Link>
            <Typography variant="body2" sx={bottomTextSx}>
              <b>Don&apos;t Miss Out!</b>
            </Typography>
          </>
        )}
      </>
    );
  };

  return (
    <>
      {loading && <BackdropLoader />}

      {userProfile && overview && (
        <>
          {/* Join Now Countdown should be displayed to:
          - Standard with Auto Renew Off
          - Standard Expired with Auto Renew Off
          - Trial
          - Trial Expired
          - Locked
          - Standard Incomplete

          Price Increase should be displayed to:
          - Standard with Auto Renew Off
          - Standard Expired with Auto Renew Off
          - Trial
          - Trial Expired
          - Locked
          - Standard Incomplete
 */}

          {/* {!discountedStandardProduct &&
            userProfile.memberStatusId !== MEMBER_STATUS_STANDARD &&
            !overview.doors.annualUpgradeOpen && (
              <>
                <DoorsClosingCountdown title="You Can Join Now" disableButton />
              </>
            )}

          {!discountedStandardProduct &&
            userProfile.memberStatusId === MEMBER_STATUS_STANDARD &&
            !userProfile.productAutoRenewing &&
            !overview.doors.annualUpgradeOpen && (
              <>
                <DoorsClosingCountdown
                  title="Don't Miss Out! Enable Auto Renew Now"
                  disableButton
                />
              </>
            )} */}

          <BannerBackground>
            <Container fixedcomponent="main" maxWidth="lg">
              <div>
                <Grid container>
                  <Grid size={{ xs: 12, sm: 12 }}>
                    <Typography sx={usernameSx}>
                      {userProfile.firstName},{" "}
                    </Typography>
                    {/*
                      Switch the message around the subscripiton status
                      standard-expired - It's Time to Upgrade
                      UPDATE
                      trial-expired w/ Discount Product -  Show Countdown
                      trial-expired wo/ Discount Product -  Enjoyed Your Trial
                      standard-incomplete - Free account
                      locked - Your Membership is Unlocked
                    */}

                    {discountedStandardProduct &&
                      [
                        "trial-expired",
                        "standard-incomplete",
                        "standard-expired",
                        "new-trial-eligible",
                        "locked",
                      ].includes(userProfile.memberStatus) && (
                        <>
                          <BuildCountdownCTA
                            product={discountedStandardProduct}
                          />
                        </>
                      )}

                    {!discountedStandardProduct &&
                      userProfile.memberStatus === "standard-expired" && (
                        <>
                          <BuildCTA
                            title="Jump Back In Again"
                            subtitle="Get going again in Two Simple Steps."
                          />
                        </>
                      )}

                    {/* CHECKED */}
                    {!discountedStandardProduct &&
                      userProfile.memberStatus === "trial-expired" && (
                        <>
                          <BuildCTA
                            title="Enjoyed Your Trial?"
                            subtitle="Want More? Join Now in Two Simple Steps."
                          />
                        </>
                      )}

                    {/* CHECKED */}
                    {!discountedStandardProduct &&
                      userProfile.memberStatus === "standard-incomplete" && (
                        <>
                          <BuildCTA
                            title="Free account"
                            subtitle="Join Now in Two Simple Steps."
                          />
                        </>
                      )}
                    {/* CHECKED */}
                    {!discountedStandardProduct &&
                      userProfile.memberStatus === "locked" && (
                        <>
                          <BuildCTA
                            title="We're Open!"
                            subtitle={`Join Now in Two Simple Steps`}
                          />
                        </>
                      )}
                    {!discountedStandardProduct &&
                      userProfile.memberStatus === "new-trial-eligible" && (
                        <>
                          <BuildCTA
                            title="We're Open!"
                            subtitle={`Join Now in Two Simple Steps`}
                          />
                        </>
                      )}
                  </Grid>
                </Grid>
              </div>
            </Container>
          </BannerBackground>
        </>
      )}
    </>
  );
};

export default CTABanner;
